import { useRef, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowUpRight, Loader2, Plus } from "lucide-react";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { smbPackageListResponseSchema } from "@shared/smb-packages";
import {
  smbClientIntakeCreateOrderResponseSchema,
  smbClientIntakeCreateOrderSchema,
  type SmbClientIntakeAdminDetail,
  type SmbClientIntakeCreateOrderResponse,
} from "@shared/smb-client-intake-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

type Props = {
  id: string;
  detail: SmbClientIntakeAdminDetail;
  onRefresh: () => Promise<void>;
  initiallyOpen?: boolean;
  onCreated?: (orderId: string) => void;
  onCancel?: () => void;
};

const errorText = (error: unknown) => {
  const fallback = "The request could not be completed.";
  if (!(error instanceof Error)) return fallback;
  const match = error.message.match(/^\d{3}: ([\s\S]*)$/);
  if (!match) return error.message;
  try {
    const body: unknown = JSON.parse(match[1]);
    if (body && typeof body === "object" && "code" in body && typeof body.code === "string" &&
        "message" in body && typeof body.message === "string" && body.message.length <= 2000)
      return body.message;
  } catch { /* Do not show raw response bodies in the dialog. */ }
  return fallback;
};

export function ClientIntakeCreateOrder({ id, detail, onRefresh, initiallyOpen = false, onCreated, onCancel }: Props) {
  const [open, setOpen] = useState(initiallyOpen);
  const [packageId, setPackageId] = useState("");
  const [runProvision, setRunProvision] = useState(true);
  const [includePhotos, setIncludePhotos] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshWarning, setRefreshWarning] = useState<string | null>(null);
  const [created, setCreated] = useState<SmbClientIntakeCreateOrderResponse | null>(null);
  const requestInFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const submission = detail.submission;
  const isDraft = submission.status === "draft";
  const existingBrand = Boolean(detail.linkedBrandId || submission.linkedBrandId || submission.brand_id);
  const linkedOrderId = detail.linkedOrderId || submission.linkedOrderId || null;
  const isLinked = Boolean(created || linkedOrderId || detail.linkedBrandId || submission.linkedBrandId || submission.brand_id || submission.hasLinkedOrder);
  const packagesQuery = useQuery({
    queryKey: ["/api/admin/smb/packages", 1, 100],
    queryFn: async () => smbPackageListResponseSchema.parse(await parseJsonResponse(await apiRequest("GET", "/api/admin/smb/packages?page=1&pageSize=100"))),
    enabled: open,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    gcTime: 0,
  });
  const effectivePackageId = packageId || packagesQuery.data?.packages.find(item => item.isDefault && item.active)?.id || "";
  const warnings = [
    !detail.assets.some(asset => asset.kind === "logo") ? "No confirmed logo has been uploaded." : null,
    !detail.answers.website_url?.trim() && detail.answers.no_website !== true ? "Website is missing and no_website was not confirmed." : null,
    !detail.services.some(service => service.name?.trim()) ? "No services were provided." : null,
    !detail.answers.contact_email?.trim() ? "Contact email is missing." : null,
  ].filter((warning): warning is string => warning !== null);
  const orderHref = linkedOrderId ? `/admin/smb/orders/${encodeURIComponent(linkedOrderId)}` : created ? `/admin/smb/orders/${encodeURIComponent(created.orderId)}` : null;
  const brandId = detail.linkedBrandId || submission.linkedBrandId || submission.brand_id;
  const brandHref = brandId && detail.submission.brand ? `/brands/${encodeURIComponent(detail.submission.brand.id)}` : null;

  if (isLinked) {
    return <div className="flex flex-wrap items-center gap-2">
      {(linkedOrderId || created) && <Badge variant="secondary">Linked</Badge>}
      {orderHref && <a href={orderHref} referrerPolicy="no-referrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-4 hover:underline">Open SMB order <ArrowUpRight className="h-3.5 w-3.5" /></a>}
      {existingBrand && brandHref && <Link href={brandHref} referrerPolicy="no-referrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-4 hover:underline">Open brand <ArrowUpRight className="h-3.5 w-3.5" /></Link>}
      {existingBrand && !linkedOrderId && <p className="w-full text-xs text-muted-foreground">This submission already has a brand. Creating or attaching another order is not supported.</p>}
      {created?.alreadyLinked && <p className="w-full text-xs text-muted-foreground">The submission was already linked. The latest order link is shown here.</p>}
      {refreshWarning && <p role="status" className="w-full text-xs text-muted-foreground">{refreshWarning}</p>}
      {created && runProvision && <p role="status" className="w-full text-xs text-muted-foreground">The order is saved. Open it to review provision and apply, and retry either step there if needed. No later steps were started.</p>}
      {initiallyOpen && <Button variant="outline" size="sm" onClick={onCancel}>Close</Button>}
    </div>;
  }

  const submit = async () => {
    if (requestInFlight.current || isDraft || existingBrand) return;
    requestInFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const body = smbClientIntakeCreateOrderSchema.parse({
         ...(effectivePackageId ? { package: effectivePackageId } : {}),
        runProvision,
        includePhotos,
      });
      const result = smbClientIntakeCreateOrderResponseSchema.parse(await parseJsonResponse(
        await apiRequest("POST", `/api/admin/smb/client-intake/${encodeURIComponent(id)}/create-order`, body),
      ));
      setCreated(result);
      try {
        await onRefresh();
      } catch {
        setRefreshWarning("The order was created, but intake links could not be refreshed. Refresh this submission to confirm its current links.");
      }
      setOpen(false);
      onCreated?.(result.orderId);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      requestInFlight.current = false;
      setPending(false);
    }
  };

  return <div className="flex flex-wrap items-center gap-2">
    {isDraft
      ? <span title="Submit the intake before creating an SMB order"><Button type="button" size="sm" disabled><Plus className="mr-1.5 h-3.5 w-3.5" />Create client</Button></span>
      : !initiallyOpen && <Button type="button" size="sm" onClick={() => { setError(null); setOpen(true); }}><Plus className="mr-1.5 h-3.5 w-3.5" />Create client</Button>}
    <Dialog open={open} onOpenChange={nextOpen => { if (!requestInFlight.current) { setOpen(nextOpen); if (!nextOpen) { setError(null); onCancel?.(); } } }}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogTitle>Create client</DialogTitle>
        <DialogDescription className="mt-1 text-sm leading-relaxed text-muted-foreground">This creates a live SMB order for {submission.business_name || "this client"} for the smb-clients workspace. With provisioning off, no brand is created yet. Provisioning now runs only provision and apply. No research or paid generation will run here.</DialogDescription>
        {warnings.length > 0 && <div className="mt-4 rounded-lg border bg-muted/40 p-3">
          <p className="flex items-center gap-2 text-xs font-semibold"><AlertTriangle className="h-4 w-4" />Review before creating</p>
          <ul className="mt-2 list-inside list-disc space-y-1 text-xs leading-relaxed text-muted-foreground">{warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>
        </div>}
        <label className="mt-4 grid gap-1.5 text-xs font-medium">Package <span className="text-muted-foreground">Optional, use the system default</span>
          <select aria-label="Package" value={effectivePackageId} onChange={event => setPackageId(event.target.value)} disabled={packagesQuery.isLoading || packagesQuery.isError} className="h-10 rounded-md border bg-background px-3 text-sm">
            <option value="">Use default</option>
            {packagesQuery.data?.packages.filter(item => item.active).map(item => <option key={item.id} value={item.id}>{item.name}{item.isDefault ? " (default)" : ""}</option>)}
          </select>
        </label>
        {packagesQuery.isLoading && <div aria-label="Loading packages" className="mt-2 h-8 animate-pulse rounded-md bg-muted/70" />}
        {packagesQuery.isError && <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-destructive/25 bg-destructive/5 p-2 text-xs text-destructive"><span>Packages could not be loaded.</span><Button variant="outline" size="sm" onClick={() => void packagesQuery.refetch()}>Retry</Button></div>}
        <label className="mt-4 grid gap-1.5 text-xs font-medium">Assets to copy
          <select aria-label="Assets to copy" value={includePhotos ? "logos-and-photos" : "logos-only"} onChange={event => setIncludePhotos(event.target.value === "logos-and-photos")} disabled={pending} className="h-10 rounded-md border bg-background px-3 text-sm">
            <option value="logos-only">Logos only</option>
            <option value="logos-and-photos">Logos and photos</option>
          </select>
        </label>
        <p className="text-xs leading-relaxed text-muted-foreground">{includePhotos
          ? "Confirmed supported logos and photos will be copied. Existing limits of 5 logos and 3 photos apply; no assets are silently omitted."
          : "Confirmed supported logos will be used. Photos stay on the original intake and are not copied to the order or brand. No later photo import is scheduled."}</p>
        <label className="mt-4 flex items-start gap-2 rounded-lg border p-3 text-xs leading-relaxed">
          <input aria-label="Run provision and apply" type="checkbox" checked={runProvision} onChange={event => setRunProvision(event.target.checked)} className="mt-0.5 accent-primary" />
          <span><span className="block font-semibold">Provision brand now</span><span className="text-muted-foreground">Optional. Runs provision and apply only, with no research or paid generation.</span></span>
        </label>
        {error && <p role="alert" className="mt-3 rounded-md border border-destructive/25 bg-destructive/5 px-3 py-2 text-xs text-destructive">{error}</p>}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" disabled={pending} onClick={() => { setOpen(false); onCancel?.(); }}>Cancel</Button>
          <Button type="button" disabled={pending || packagesQuery.isLoading || packagesQuery.isError} onClick={() => void submit()}>{pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm</Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>;
}
